import { useCallback, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { useMutation, useQueryClient } from '@tanstack/react-query'
import { useApp } from '@/app/AppContext'
import { can } from '@/auth/permissions'
import { ApiError } from '@/lib/api'
import { AccessDenied } from '@/pages/AccessDenied'
import { createCounterparty, type CreateCounterpartyRequest } from './counterpartyApi'
import { blankToNull, CreateField, CreateModal } from './CreateModal'
import { DuplicatePanel } from './DuplicatePanel'

/**
 * Заведение контрагента с поиском дублей **до** сохранения (тикет #70, ТЗ §4.2), вёрстка
 * по CreateRecord.dc.html (форма `company`).
 *
 * ## Что здесь главное
 *
 * **Дубли ищутся, пока оператор печатает, а не после нажатия «Создать».** Найденный
 * после сохранения дубль уже успел собрать на себя сделку, счёт и звонок; слияние тогда —
 * перенос истории, а не отмена опечатки.
 *
 * **Похожесть не блокирует, точное совпадение пары `(inn, kpp)` — блокирует.** Филиал
 * одного юрлица делит ИНН с головной организацией и различается КПП: отказать по ИНН
 * значило бы, что филиал завести нельзя. Сервер помечает блокирующий случай флагом, и
 * кнопка гасится ровно по нему.
 *
 * **Пустые поля уходят null** (`blankToNull`): пустая строка ИНН попадает в частичный
 * уникальный индекс, и второе физлицо без ИНН падало бы на первом.
 *
 * Из макета нет «Заполнить по ИНН» (запроса в ЕГРЮЛ у сервера нет — пометки «из ЕГРЮЛ»
 * были бы выдумкой), выбора ответственного и тегов (заведение их не принимает: ответственным
 * становится заводящий, теги ставятся на карточке).
 */

const KINDS = ['legal', 'sole_trader', 'individual'] as const

const EMPTY: CreateCounterpartyRequest = {
  name: '',
  kind: 'legal',
  relation: 'client',
  inn: '',
  kpp: '',
  ogrn: '',
  legalName: '',
  legalAddress: '',
  actualAddress: '',
  email: '',
  phone: '',
  website: '',
}

export function CounterpartyCreatePage() {
  const { t, user } = useApp()
  const navigate = useNavigate()
  const queryClient = useQueryClient()

  const [form, setForm] = useState<CreateCounterpartyRequest>(EMPTY)
  const [blocking, setBlocking] = useState(false)
  const [notice, setNotice] = useState<string | null>(null)

  const set = useCallback((field: keyof CreateCounterpartyRequest, value: string) => {
    setForm((previous) => ({ ...previous, [field]: value }))
  }, [])

  const individual = form.kind === 'individual'

  const save = useMutation({
    // Поля физлица уходят только у физлица: ФИО, набранное и брошенное после смены вида,
    // иначе осталось бы на карточке юрлица, где его никто не покажет и не сотрёт.
    mutationFn: () => createCounterparty(blankToNull(individual ? form : withoutPerson(form))),
    onSuccess: (intake) => {
      void queryClient.invalidateQueries({ queryKey: ['list', 'sales.counterparty'] })
      navigate(`/counterparties/${intake.counterparty.id}`)
    },
    onError: (error) => {
      // Отказ сервера показывается дословно: он называет существующую карточку, и это
      // единственное, что оператору нужно, чтобы открыть её вместо заведения второй.
      setNotice(error instanceof ApiError ? error.message : t('app.error'))
    },
  })

  // Проверка на маршруте, а не только в меню: прямая ссылка минует навигацию.
  if (!can(user, 'sales.counterparty', 'create')) return <AccessDenied />

  const blockedReason = blocking
    ? t('card.cp.create.blocked')
    : form.name.trim() === ''
      ? t('create.cp.needName')
      : null

  const text = (field: keyof CreateCounterpartyRequest, id: string, label: string, options?: {
    required?: boolean; span?: boolean; help?: string; mono?: boolean; type?: string; inputMode?: 'numeric'
  }) => (
    <CreateField id={id} label={label} required={options?.required} span={options?.span} help={options?.help}>
      <input
        id={id}
        className={options?.mono ? 'input mono' : 'input'}
        type={options?.type ?? 'text'}
        inputMode={options?.inputMode}
        value={(form[field] as string | undefined) ?? ''}
        required={options?.required}
        onChange={(event) => set(field, event.target.value)}
      />
    </CreateField>
  )

  return (
    <CreateModal
      overline={t('nav.counterparties')}
      title={t('card.cp.create.title')}
      error={notice}
      blockedReason={blockedReason}
      pending={save.isPending}
      submitLabel={save.isPending ? t('create.saving') : t('card.cp.create.submit')}
      onCancel={() => navigate('/counterparties')}
      onSubmit={() => {
        setNotice(null)
        save.mutate()
      }}
      more={{
        hint: t('create.cp.more.hint'),
        content: (
          <>
            {text('phone', 'phone', t('column.phone'))}
            {text('email', 'email', t('column.email'))}
            {text('website', 'website', t('create.cp.website'))}
            {text('legalName', 'legalName', t('card.cp.create.legalName'))}
            {text('actualAddress', 'actualAddress', t('card.cp.create.actualAddress'), { span: true })}
          </>
        ),
      }}
    >
      <div className="form-grid">
        {/* ИНН и КПП рядом: уникальность контрагента — по паре, а не по ИНН. */}
        {text('inn', 'inn', t('column.inn'), { span: true, mono: true, inputMode: 'numeric', help: t('create.cp.innHelp') })}

        {/* Панель стоит НАД кнопкой и рядом с ИНН: предупреждение, которое видно только
            после прокрутки, читают уже после нажатия. */}
        <DuplicatePanel
          variant="form"
          criteria={{ inn: form.inn, kpp: form.kpp, phone: form.phone, email: form.email }}
          onBlockingChange={setBlocking}
        />

        <CreateField label={t('column.kind')} span>
          <div className="seg" role="group" aria-label={t('column.kind')} style={{ alignSelf: 'flex-start' }}>
            {KINDS.map((kind) => (
              <button key={kind} type="button" aria-pressed={form.kind === kind} onClick={() => set('kind', kind)}>
                {t(`counterparty.kind.${kind}`)}
              </button>
            ))}
          </div>
        </CreateField>

        {text('name', 'name', t('column.name'), { required: true })}
        {text('kpp', 'kpp', t('column.kpp'), { mono: true, inputMode: 'numeric' })}
        {!individual && text('ogrn', 'ogrn', t('create.cp.ogrn'), { mono: true, inputMode: 'numeric' })}
        {text('legalAddress', 'legalAddress', t('card.cp.create.legalAddress'), { span: true })}

        {individual && (
          <>
            {text('lastName', 'lastName', t('column.last_name'))}
            {text('firstName', 'firstName', t('column.first_name'))}
            {text('middleName', 'middleName', t('column.middle_name'))}
            {text('birthDate', 'birthDate', t('column.birth_date'), { type: 'date' })}
            {/* Согласие — отдельный явный флажок, а не умолчание: 152-ФЗ требует, чтобы его
                дали, а не чтобы его не отозвали. */}
            <label className="row span-2" style={{ gap: 8 }}>
              <input
                type="checkbox"
                checked={form.consentGiven === true}
                onChange={(event) =>
                  setForm((previous) => ({ ...previous, consentGiven: event.target.checked }))
                }
              />
              {t('card.cp.consent')}
            </label>
          </>
        )}
      </div>
    </CreateModal>
  )
}

function withoutPerson(form: CreateCounterpartyRequest): CreateCounterpartyRequest {
  const { lastName: _l, firstName: _f, middleName: _m, birthDate: _b, consentGiven: _c, ...rest } = form
  return rest
}
